import { useState, useEffect } from 'react';
import { useFhirContext } from '../contexts/FhirContextProvider';

export type UserType = 'IKC' | 'DKC' | 'UNKNOWN';

interface UseUserTypeResult {
  userType: UserType;
  isReadOnly: boolean;
  loading: boolean;
  userEmail: string | null;
  userName: string | null;
  userId: string | null;
}

export const useUserType = (): UseUserTypeResult => {
  const { client, isReady, error, user } = useFhirContext();
  const [userType, setUserType] = useState<UserType>('UNKNOWN');
  const [loading, setLoading] = useState(true);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserType = async () => {
      if (!isReady) {
        return;
      }

      // If SMART context is not available (development mode), use user from context if available
      if (error || !client) {
        console.warn('⚠️ No SMART context - Running in DEVELOPMENT MODE');
        console.warn('   Defaulting to IKC user with full permissions');
        setUserType('IKC');
        
        // Use user data from context (set by FhirContextProvider in dev mode)
        if (user?.name) {
          setUserName(user.name);
          console.log('👤 Using dev user name:', user.name);
        } else {
          setUserName('Development User');
        }
        
        if (user?.id) {
          setUserId(user.id);
          console.log('👤 Using dev user id:', user.id);
        } else {
          setUserId('alexo123');
        }

        if (user?.email) {
          setUserEmail(user.email);
        } else {
          setUserEmail('dev-user@davita.com');
        }
        
        setLoading(false);
        return;
      }

      try {
        // For now, default to IKC user
        // TODO: Implement user type detection from FHIR Practitioner resource
        console.log('✅ SMART context available, defaulting to IKC user');
        
        // Get user email and name from context (already fetched in FhirContextProvider)
        if (user?.name) {
          setUserName(user.name);
          console.log('👤 User name:', user.name);
        } else {
          setUserName('SMART User');
          console.warn('⚠️ No name found in user data');
        }

        if (user?.email) {
          setUserEmail(user.email);
          console.log('📧 User email:', user.email);
        } else {
          setUserEmail('smart-user@davita.com');
          console.warn('⚠️ No email found in user data');
        }
        
        // Set user type - default to IKC for now
        setUserType('IKC');
        
      } catch (error) {
        console.error('Error fetching user type:', error);
        // Default to IKC for development
        setUserType('IKC');
        setUserEmail('error-fallback@davita.com');
        setUserName('Error User');
        setUserId('error-user-id');
      } finally {
        setLoading(false);
      }
    };

    fetchUserType();
  }, [client, isReady, error, user]);

  return {
    userType,
    isReadOnly: userType === 'DKC',
    loading,
    userEmail,
    userName,
    userId
  };
};

